import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, X } from "lucide-react";

const imgSrc = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
};

/**
 * VideoBadge — boutique "tap to watch" round badge that overlays the
 * product gallery (Myntra-style). The badge uses the primary product
 * photo as its thumbnail with a play overlay and animated ring, so the
 * customer never sees a black <video> tile while bytes are downloading.
 *
 * Tapping the badge opens a fullscreen lightbox that autoplays the clip
 * looped and SILENTLY by boutique design policy — there is intentionally
 * no unmute affordance. The <video> element is only mounted on open,
 * so the rest of the PDP loads at full speed — no impact on initial
 * paint or image network priority.
 *
 * Props:
 *   - videoUrl       (required) — R2/CDN url of the .mp4
 *   - posterUrl      (required) — first product photo used as thumbnail/poster
 *   - className?     — extra wrapper classes (default: absolute bottom-3 right-3)
 */
export default function VideoBadge({ videoUrl, posterUrl, className = "" }) {
    const [open, setOpen] = useState(false);
    if (!videoUrl) return null;

    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                aria-label="Watch product video"
                data-testid="pdp-video-badge"
                className={`absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-20 group animate-video-nudge ${className}`}
                style={{ transformOrigin: "50% 100%" }}
            >
                {/* Pulsing halos so the badge is spottable even against busy
                    or light photo backgrounds. Two rings at slightly different
                    speeds give a subtle "watch me" cadence without feeling busy. */}
                <span className="absolute inset-0 rounded-full bg-rosegold/40 animate-ping-slow pointer-events-none" aria-hidden />
                <span className="absolute inset-0 rounded-full bg-wine/30 animate-ping-slower pointer-events-none" aria-hidden />

                {/* Solid rosegold→wine gradient ring + dark contrast outline so
                    the round always reads even when the photo behind it is also
                    pale/ivory. */}
                <span className="absolute -inset-1 rounded-full bg-gradient-to-br from-rosegold via-wine to-rosegold shadow-lg shadow-black/30" aria-hidden />
                <span className="absolute -inset-[5px] rounded-full ring-2 ring-black/15" aria-hidden />

                <span className="relative block w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-full overflow-hidden bg-stone-200 border-2 border-white shadow-xl shadow-black/40">
                    <img
                        src={imgSrc(posterUrl) || "/default-item.svg"}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover object-top"
                        loading="lazy"
                        onError={(e) => { e.target.src = "/default-item.svg"; }}
                    />
                    {/* Soft dark veil + play glyph */}
                    <span className="absolute inset-0 bg-gradient-to-t from-black/45 to-black/10" aria-hidden />
                    <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/95 text-stone-900 flex items-center justify-center shadow-md">
                            <Play size={14} fill="currentColor" className="translate-x-[1px]" />
                        </span>
                    </span>
                </span>
            </button>

            {open && (
                <VideoLightbox
                    videoUrl={videoUrl}
                    posterUrl={posterUrl}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
}

/**
 * Fullscreen player — boutique-clean, no native browser controls.
 * Closes on overlay tap, ESC, or the X button. Volume toggle in
 * top-right; the clip loops silently by default (browser-policy safe).
 */
function VideoLightbox({ videoUrl, posterUrl, onClose }) {
    const videoRef = useRef(null);
    const [ready, setReady] = useState(false);     // canPlayThrough has fired at least once
    const [buffering, setBuffering] = useState(false); // mid-stream stall
    const [needsTap, setNeedsTap] = useState(false);
    const [progressPct, setProgressPct] = useState(0); // 0–100, downloading visualisation

    // ESC to close + lock body scroll while open
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    // Update download-progress %. Computed from `buffered` ranges /
    // `duration` so the customer sees real movement (not a fake spinner).
    const onProgress = () => {
        const v = videoRef.current;
        if (!v || !v.duration || isNaN(v.duration)) return;
        const buf = v.buffered;
        if (buf.length === 0) return;
        const end = buf.end(buf.length - 1);
        const pct = Math.min(100, Math.round((end / v.duration) * 100));
        setProgressPct(pct);
    };

    // Fires once we have enough buffer for SMOOTH playback (vs `canplay`
    // which fires the instant 1 frame is decoded — root cause of "freezes
    // immediately after first frame" symptoms).
    const onCanPlayThrough = () => {
        setReady(true);
        setBuffering(false);
        videoRef.current?.play?.().catch(() => setNeedsTap(true));
    };

    // Safety net: some browsers don't reliably emit `canplaythrough` on
    // streamed CDN clips. If `canplay` AND we have ≥25% buffered, start
    // anyway so the customer never sees a forever-loader.
    const onCanPlay = () => {
        const v = videoRef.current;
        if (!v) return;
        const buf = v.buffered;
        const headroom = buf.length > 0 && v.duration ? buf.end(buf.length - 1) / v.duration : 0;
        if (headroom >= 0.25 || ready) {
            setReady(true);
            setBuffering(false);
            v.play?.().catch(() => setNeedsTap(true));
        }
    };

    // Buffer ran dry mid-playback → re-show the boutique loader so the
    // user knows we're fetching, not stuck on a still image.
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);

    // Hard timeout — if neither event has fired in 12 s, just play and let
    // the browser stream as it goes. The loader bails out as a last resort.
    useEffect(() => {
        const t = setTimeout(() => {
            if (!ready) {
                setReady(true);
                videoRef.current?.play?.().catch(() => setNeedsTap(true));
            }
        }, 12000);
        return () => clearTimeout(t);
    }, [ready]);

    const tapToPlay = (e) => {
        e.stopPropagation();
        videoRef.current?.play?.().then(() => setNeedsTap(false)).catch(() => {});
    };

    const showLoader = !ready || buffering;
    const loaderCaption = !ready
        ? (progressPct > 0 ? `Loading boutique preview · ${progressPct}%` : "Loading your boutique preview…")
        : "Buffering…";

    const portalRoot = typeof document !== "undefined" ? document.body : null;
    if (!portalRoot) return null;

    return createPortal(
        <div
            data-testid="pdp-video-lightbox"
            className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center animate-fadeIn"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
        >
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                aria-label="Close video"
                data-testid="pdp-video-lightbox-close"
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/95 text-stone-900 hover:bg-rosegold hover:text-white flex items-center justify-center shadow-2xl shadow-black/50 z-30"
            >
                <X size={20} strokeWidth={2.5} />
            </button>

            <div className="relative w-full max-w-[100vw] sm:max-w-[92vw] h-[100svh] sm:h-auto sm:max-h-[92vh] aspect-auto sm:aspect-[3/4] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                {/* Boutique loading / buffering screen — replaces the bare
                    poster with a branded shimmer so the wait feels intentional
                    rather than "broken thumbnail". Shows again during mid-stream
                    stalls so the customer never sees a frozen still. */}
                {showLoader && (
                    <div
                        data-testid="pdp-video-lightbox-loading"
                        className="absolute inset-0 rounded-lg overflow-hidden flex items-center justify-center bg-gradient-to-br from-ivory via-ivory-soft to-stone-200 z-10"
                    >
                        {/* Subtle moving sheen for richness */}
                        <div className="absolute inset-0 opacity-60 animate-shimmer-slow pointer-events-none"
                             style={{
                                 backgroundImage:
                                     "linear-gradient(110deg, transparent 30%, rgba(196, 154, 108, 0.18) 50%, transparent 70%)",
                                 backgroundSize: "200% 100%",
                             }} aria-hidden />
                        {/* Soft animated rings around the brand mark */}
                        <span className="absolute w-44 h-44 rounded-full border border-rosegold/30 animate-ping-slow" aria-hidden />
                        <span className="absolute w-32 h-32 rounded-full border border-rosegold/40 animate-ping-slower" aria-hidden />

                        <div className="relative z-10 text-center px-6">
                            <div className="relative w-20 h-20 mx-auto mb-5">
                                <span className="absolute inset-0 rounded-full border-[3px] border-rosegold/20" aria-hidden />
                                <span className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-rosegold border-r-wine animate-spin" style={{ animationDuration: "1.4s" }} aria-hidden />
                                <span className="absolute inset-2 rounded-full bg-white shadow-md flex items-center justify-center font-display text-lg text-wine tracking-[0.04em]">
                                    JnS
                                </span>
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.4em] text-rosegold font-bold mb-2">
                                A moment
                            </p>
                            <h3 className="font-display text-xl sm:text-2xl text-ink font-light leading-snug">
                                Bringing the <em className="not-italic text-wine">drape</em> to life
                            </h3>
                            <p className="mt-2 text-[11.5px] text-ink-soft" data-testid="pdp-video-lightbox-progress">
                                {loaderCaption}
                            </p>
                        </div>

                        {/* Bottom progress bar — concrete feedback that bytes
                            are coming, not a generic spinner. */}
                        {progressPct > 0 && !ready && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-rosegold/15">
                                <div
                                    className="h-full bg-gradient-to-r from-rosegold to-wine transition-all duration-300"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <video
                    ref={videoRef}
                    src={imgSrc(videoUrl)}
                    poster={posterUrl ? imgSrc(posterUrl) : undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onCanPlay={onCanPlay}
                    onCanPlayThrough={onCanPlayThrough}
                    onProgress={onProgress}
                    onLoadedMetadata={onProgress}
                    onWaiting={onWaiting}
                    onPlaying={onPlaying}
                    className={`relative max-w-full max-h-full sm:rounded-lg shadow-2xl transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0"}`}
                    data-testid="pdp-video-lightbox-player"
                />
                {needsTap && ready && (
                    <button
                        type="button"
                        onClick={tapToPlay}
                        aria-label="Play"
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        <span className="w-16 h-16 rounded-full bg-white/95 text-stone-900 flex items-center justify-center shadow-2xl">
                            <Play size={26} fill="currentColor" />
                        </span>
                    </button>
                )}
            </div>
        </div>,
        portalRoot
    );
}
